import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES, changeLanguage } from "@/lib/i18n";

/**
 * Small floating language switcher for logged-out visitors -- signed-in users get the navbar
 * globe icon instead (Header-Right/HeaderRight.js); this widget is deliberately only mounted
 * for the logged-out case (see HeaderContainer.js). Fixed bottom-right, mirroring the existing
 * bottom-left dark-mode switcher (dark-switch.js / .my_switcher) in weight and treatment without
 * colliding with it.
 */
const LanguageFloatingWidget = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  return (
    <div className="rbt-lang-float" ref={rootRef}>
      {open && (
        <div className="rbt-lang-float__panel" role="dialog" aria-label={t("settings.language.title")}>
          <div className="rbt-lang-switcher rbt-lang-switcher--float" role="radiogroup" aria-label={t("settings.language.title")}>
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isActive = lang.code === current.code;
              return (
                <button
                  type="button"
                  key={lang.code}
                  role="radio"
                  aria-checked={isActive}
                  className={`rbt-lang-switcher__option ${isActive ? "is-active" : ""}`}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setOpen(false);
                  }}
                >
                  {lang.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <button
        type="button"
        className="rbt-lang-float__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t("settings.language.title")}
        title={t("settings.language.title")}
      >
        <i className="feather-globe"></i>
      </button>
    </div>
  );
};

export default LanguageFloatingWidget;
